import {
  Box,
  HStack,
  ListItem,
  StackProps,
  Text,
  TextProps,
  VStack,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { ReactNode } from 'react'

import { useAppStore } from '../../store/appStore/appStore'
import { colors } from '../../theme/colors'
import { DownloadLink } from '../Toast/DownloadLink'
import { NotificationItemResponse } from './NotificationsMenu'

export type NotificationItemType = {
  id?: string
  message: string
  fileId?: string
  fileName?: string
  createdDate: Date
  isRead?: boolean
}

type NotificationItemProps = {
  notification: NotificationItemResponse
}
export const NotificationItem = ({ notification }: NotificationItemProps) => {
  if (notification.category === 'PENDING') {
    return <ExportProcessingNotification notification={notification} />
  }
  if (notification.category === 'ERROR') {
    return <ExportErrorNotification notification={notification} />
  }
  if (notification.category === 'INFO') {
    return <ExportSuccessNotification notification={notification} />
  }
  return null
}

export const ExportSuccessNotification = ({
  notification,
}: NotificationItemProps) => {
  const { queryExport } = notification
  return (
    <ListItem>
      <NotificationWrapper
        backgroundColor={
          notification.isRead ? 'presets.neutral.0' : 'presets.turquoise.10'
        }
      >
        <NotificationTitle>{notification.message}</NotificationTitle>
        {queryExport && (
          <Box display="flex">
            <DownloadLink
              onClick={() =>
                useAppStore
                  .getState()
                  .notifications.downloadHandler(
                    queryExport.fileId!,
                    queryExport.fileName!,
                  )
              }
              padding="0px"
              // marginLeft="-3px"
            >
              {queryExport.fileName}
            </DownloadLink>
          </Box>
        )}

        <NotificationDate date={notification.createdDate} />
      </NotificationWrapper>
    </ListItem>
  )
}

export const ExportProcessingNotification = ({
  notification,
}: NotificationItemProps) => {
  const { queryExport } = notification

  return (
    <ListItem>
      <NotificationWrapper backgroundColor="#FAF5E3">
        <NotificationTitle>{notification.message}</NotificationTitle>

        <HStack gap="5px">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M9.00009 6V9L10.299 9.75M1.69455 10.7045C1.56728 10.1569 1.5 9.58633 1.5 9C1.5 8.41367 1.56728 7.84306 1.69455 7.29545M7.63636 1.62367C8.07856 1.54244 8.53432 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C8.53432 16.5 8.07856 16.4576 7.63636 16.3763M5.25 2.50337C4.22702 3.09513 3.35678 3.92158 2.71291 4.90909M2.71291 13.0909C3.35678 14.0784 4.22702 14.9049 5.25 15.4966"
              stroke="#062321"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <Text color="presets.turquoise.100" fontSize="12px" fontWeight="700">
            {queryExport!.fileName}
          </Text>
        </HStack>

        <NotificationDate date={notification.createdDate} />
      </NotificationWrapper>
    </ListItem>
  )
}

export const ExportErrorNotification = ({
  notification,
}: NotificationItemProps) => {
  return (
    <ListItem>
      <NotificationWrapper
        backgroundColor={notification.isRead ? 'presets.neutral.0' : '#FBECEC'}
      >
        <NotificationTitle>{notification.message}</NotificationTitle>
        <HStack gap="5px">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M15.9495 15.7483H2.04979C1.78184 15.7483 1.53425 15.6054 1.40028 15.3733C1.26631 15.1413 1.26631 14.8554 1.40029 14.6233L8.34979 2.62334C8.48386 2.39168 8.73124 2.24902 8.99891 2.24902C9.26658 2.24902 9.51396 2.39168 9.64804 2.62334L16.5975 14.6233C16.7314 14.8553 16.7315 15.141 16.5977 15.373C16.4639 15.605 16.2166 15.7481 15.9488 15.7483H15.9495ZM8.25004 11.9983V13.4983H8.94979H8.99854H9.04729H9.74854V11.9983H8.25004ZM8.25004 6.74834V10.4983H9.75004V6.74834H8.25004Z"
              fill={
                notification.isRead
                  ? colors.presets.turquoise[100]
                  : colors.presets.pink[70]
              }
            />
          </svg>
          <Text
            color="presets.turquoise.100"
            fontSize="12px"
            fontWeight={notification.isRead ? '400' : '700'}
          >
            {notification?.queryExport?.fileName}
          </Text>
        </HStack>
        <NotificationDate date={notification.createdDate} />
      </NotificationWrapper>
    </ListItem>
  )
}

const NotificationWrapper = ({
  children,
  ...props
}: { children?: ReactNode } & StackProps) => {
  return (
    <VStack
      alignItems="stretch"
      backgroundColor="presets.neutral.0"
      borderBottom="1px solid"
      borderBottomColor="presets.neutral.20"
      padding="11px 20px"
      gap="10px"
      {...props}
    >
      {children}
    </VStack>
  )
}

const NotificationTitle = (props: TextProps) => (
  <Text fontSize="12px" fontWeight="600" {...props} />
)

const NotificationDate = ({ date }: { date: string }) => {
  return (
    <HStack alignSelf="stretch" justifyContent="space-between">
      <Text color="presets.turquoise.100" fontSize="12px" fontWeight="400">
        {format(new Date(date), 'eeee, p')}
      </Text>
      <Text color="presets.turquoise.100" fontSize="12px" fontWeight="400">
        {format(new Date(date), 'PP')}
      </Text>
    </HStack>
  )
}
